import { useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, ROUTES } from '@/shared/lib/constants';
import { tagKeys } from '@/shared/lib/query/queryKeys';

import { deleteClassificationApi } from '@/features/tags/api';
import type { DeleteClassificationRequest } from '@/features/tags/api';

type UseDeleteClassificationOptions = {
  onTreeRefresh?: (parentFqn: string) => void;
};

export const useDeleteClassification = (classificationName?: string, options?: UseDeleteClassificationOptions) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutation = useMutation({
    mutationFn: (request: DeleteClassificationRequest) => deleteClassificationApi(request),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: tagKeys.categoryDetail(classificationName ?? '') });

      await queryClient.invalidateQueries({ queryKey: tagKeys.categories() });

      optionsRef.current?.onTreeRefresh?.('');

      navigate(ROUTES.TAGS, { replace: true });
      notification.success({
        message: NOTIFICATION_MESSAGES.CLASSIFICATION_DELETE_SUCCESS,
        description: NOTIFICATION_MESSAGES.CLASSIFICATION_DELETE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleDelete = useCallback(
    (classificationId: string, options?: { recursive?: boolean; hardDelete?: boolean }) => {
      return mutation.mutateAsync({
        classificationId,
        recursive: options?.recursive ?? true,
        hardDelete: options?.hardDelete ?? true,
      });
    },
    [mutation]
  );

  return {
    ...mutation,
    handleDelete,
  };
};
